import { useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchCharacter, updateCharacterClass, type ParseMetric } from '../api/characters';
import { fetchServerProgressionOverview } from '../api/progression';
import type { CharacterBossStat, CharacterExpansionGroup, NamedRef, ParseRecordSummary } from '../api/types';
import { ErrorState } from '../components/ErrorState';
import { LoadingState } from '../components/LoadingState';
import { formatDateTime, formatDecimal, formatDuration, formatNumber } from '../utils/format';
import { getActiveServerSlug, serverCharacterBossParsesPath, serverGuildPath } from '../utils/siteContext';

function parseRate(parse: ParseRecordSummary | null | undefined, metric: ParseMetric) {
  if (!parse) return 0;
  return metric === 'HEALING' ? parse.hps : parse.dps;
}

function parseAmount(parse: ParseRecordSummary, metric: ParseMetric) {
  return metric === 'HEALING' ? parse.totalHealing : parse.totalDamage;
}

function parsePetAmount(parse: ParseRecordSummary, metric: ParseMetric) {
  return metric === 'HEALING' ? parse.petHealing : parse.petDamage;
}

function parseMetricLabel(metric: ParseMetric) {
  return metric === 'HEALING' ? 'Healing' : 'Damage';
}

function encounterPath(parse: ParseRecordSummary | null | undefined) {
  return parse?.encounterId ? `/encounters/${encodeURIComponent(parse.encounterId)}` : null;
}

function EncounterValueLink({ parse, children, className = '' }: { parse: ParseRecordSummary | null | undefined; children: ReactNode; className?: string }) {
  const path = encounterPath(parse);
  if (!path) {
    return <>{children}</>;
  }

  return (
    <Link className={`link-light text-decoration-none ${className}`.trim()} to={path}>
      {children}
    </Link>
  );
}

function expansionKey(expansion: NamedRef | null | undefined) {
  return String(expansion?.slug ?? expansion?.id ?? 'unknown');
}

function partitionKey(partition: NamedRef | null | undefined) {
  return String(partition?.slug ?? partition?.id ?? 'unknown');
}

type PartitionOption = NamedRef & { isDefault?: boolean };

function progressionExpansionsFrom(data: Awaited<ReturnType<typeof fetchServerProgressionOverview>> | undefined) {
  return [...(data?.progression?.expansions ?? [])].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

function partitionOptionsForExpansion(data: Awaited<ReturnType<typeof fetchServerProgressionOverview>> | undefined, selectedExpansionKey: string | null) {
  const expansions = progressionExpansionsFrom(data);
  const selectedExpansion = expansions.find((expansion) => expansionKey(expansion) === selectedExpansionKey) ?? expansions[0] ?? null;
  const partitions = new Map<string, PartitionOption>();

  for (const tier of selectedExpansion?.tiers ?? selectedExpansion?.raidTiers ?? []) {
    for (const partition of tier.rankingPartitions ?? []) {
      const key = partitionKey(partition);
      if (!partitions.has(key)) partitions.set(key, partition);
    }
  }

  return [...partitions.values()];
}

function partitionOptionsFromAllStarRanks(ranks: Array<{ rankingPartition: NamedRef | null; includedExpansions?: Array<NamedRef | null>; expansion?: NamedRef | null }> | undefined, selectedExpansionKey: string | null) {
  const options = new Map<string, PartitionOption>();
  for (const rank of ranks ?? []) {
    const partition = rank.rankingPartition;
    if (!partition) continue;
    const matchesExpansion = !selectedExpansionKey
      || (rank.includedExpansions ?? []).some((expansion) => expansionKey(expansion) === selectedExpansionKey)
      || expansionKey(rank.expansion) === selectedExpansionKey;
    if (!matchesExpansion) continue;
    const key = partitionKey(partition);
    if (!options.has(key)) options.set(key, partition);
  }
  return [...options.values()];
}

function pct(value: number | null | undefined) {
  return Math.max(0, Math.min(100, value ?? 0));
}

function CharacterBossChart({ stats, metric }: { stats: CharacterBossStat[]; metric: ParseMetric }) {
  if (stats.length === 0) {
    return <div className="text-body-secondary">No ranked bosses for this expansion yet.</div>;
  }

  return (
    <div className="character-boss-chart character-boss-chart--horizontal" aria-label={`${parseMetricLabel(metric)} parse overview by boss`}>
      <div className="character-boss-chart__legend-row small text-body-secondary">
        <span><span className="character-boss-chart__legend character-boss-chart__legend--best" /> Best</span>
        <span><span className="character-boss-chart__legend character-boss-chart__legend--avg" /> Avg</span>
      </div>
      {stats.map((stat) => {
        const best = pct(stat.bestPercentile);
        const average = pct(stat.averagePercentile);
        const bossName = stat.boss?.name ?? 'Unknown Boss';
        return (
          <div className="character-boss-chart__row" key={String(stat.boss?.id ?? stat.boss?.name ?? bossName)}>
            <div className="character-boss-chart__boss text-truncate" title={bossName}>{bossName}</div>
            <div className="character-boss-chart__track" aria-label={`${bossName} best ${best}, average ${average}`}>
              <div className="character-boss-chart__bar character-boss-chart__bar--avg" style={{ width: `${average}%` }} />
              <div className="character-boss-chart__bar character-boss-chart__bar--best" style={{ width: `${best}%` }} />
            </div>
            <div className="character-boss-chart__numbers small text-body-secondary">
              <span className="tabular-nums">{stat.bestPercentile === null || stat.bestPercentile === undefined ? 'N/A' : `${formatDecimal(stat.bestPercentile, 0)}%`}</span>
              <span className="tabular-nums">{stat.averagePercentile === null || stat.averagePercentile === undefined ? 'N/A' : `${formatDecimal(stat.averagePercentile, 0)}%`}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function CharacterDetailPage() {
  const { id = '' } = useParams();
  const serverSlug = getActiveServerSlug();
  const [metric, setMetric] = useState<ParseMetric>('DAMAGE');
  const [activeExpansionKey, setActiveExpansionKey] = useState<string | null>(null);
  const [activePartitionKey, setActivePartitionKey] = useState<string | null>(null);
  const [classDraft, setClassDraft] = useState<string>('');
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['character', id, metric],
    queryFn: () => fetchCharacter(id, metric),
    enabled: id.length > 0,
    placeholderData: keepPreviousData,
  });
  const progressionQuery = useQuery({
    queryKey: ['server-progression-overview', serverSlug],
    queryFn: () => fetchServerProgressionOverview(serverSlug),
    enabled: Boolean(serverSlug),
    placeholderData: keepPreviousData,
  });

  const classMutation = useMutation({
    mutationFn: (className: string | null) => updateCharacterClass(id, className),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['character', id] });
    },
  });

  const expansionGroups = query.data?.expansionGroups ?? [];
  const expansionGroupByKey = useMemo(() => new Map(expansionGroups.map((group) => [expansionKey(group.expansion), group])), [expansionGroups]);
  const unlockedExpansionTabs = useMemo(() => {
    const progressionExpansions = progressionExpansionsFrom(progressionQuery.data);
    if (progressionExpansions.length === 0) return expansionGroups.map((group) => group.expansion).filter((expansion): expansion is NamedRef => Boolean(expansion));
    return progressionExpansions;
  }, [expansionGroups, progressionQuery.data]);
  const activeExpansion = useMemo<CharacterExpansionGroup | null>(() => {
    const selectedKey = activeExpansionKey ?? expansionKey(unlockedExpansionTabs[0]);
    if (!selectedKey || selectedKey === 'unknown') return expansionGroups[0] ?? null;
    return expansionGroupByKey.get(selectedKey) ?? { expansion: unlockedExpansionTabs.find((expansion) => expansionKey(expansion) === selectedKey) ?? null, bossStats: [] };
  }, [activeExpansionKey, expansionGroupByKey, expansionGroups, unlockedExpansionTabs]);
  const partitionOptions = useMemo(() => {
    const selectedExpansionKey = expansionKey(activeExpansion?.expansion);
    const fromProgression = partitionOptionsForExpansion(progressionQuery.data, selectedExpansionKey);
    const fromRanks = partitionOptionsFromAllStarRanks(query.data?.allStarRanks, selectedExpansionKey);
    const merged = new Map<string, PartitionOption>();
    for (const partition of fromProgression) merged.set(partitionKey(partition), partition);
    for (const partition of fromRanks) if (!merged.has(partitionKey(partition))) merged.set(partitionKey(partition), partition);
    return [...merged.values()];
  }, [activeExpansion?.expansion, progressionQuery.data, query.data?.allStarRanks]);
  const selectedPartition = partitionOptions.find((partition) => partitionKey(partition) === activePartitionKey)
    ?? partitionOptions.find((partition) => partition.isDefault)
    ?? partitionOptions[partitionOptions.length - 1]
    ?? null;

  if (query.isLoading && !query.data) return <LoadingState label="Loading character" />;
  if (query.isError) return <div className="content-wrapper page-main"><ErrorState title="Could not load character" error={query.error} /></div>;
  if (!query.data) return null;

  const { character } = query.data;
  const recentParses = query.data.recentParses ?? [];
  const allStarRanks = query.data.allStarRanks ?? [];
  const activeBossStats = activeExpansion?.bossStats ?? [];
  const activeBestParses = activeBossStats.map((stat) => stat.bestParse).filter((parse): parse is ParseRecordSummary => Boolean(parse));
  const rateLabel = metric === 'HEALING' ? 'HPS' : 'DPS';
  const amountLabel = metric === 'HEALING' ? 'Healing' : 'Damage';
  const petLabel = metric === 'HEALING' ? 'Pet Heal' : 'Pet Damage';
  const bestOverall = activeBestParses.reduce<ParseRecordSummary | null>((best, parse) => {
    if (!best) return parse;
    if ((parse.percentile ?? -1) > (best.percentile ?? -1)) return parse;
    if (parse.percentile === best.percentile && parseRate(parse, metric) > parseRate(best, metric)) return parse;
    return best;
  }, null);
  const avgPercentile = activeBossStats.length > 0
    ? activeBossStats.reduce((sum, stat) => sum + (stat.averagePercentile ?? 0), 0) / activeBossStats.length
    : 0;
  const activeAllStarRanks = allStarRanks.filter((rank) => {
    const selectedExpansionId = activeExpansion?.expansion?.id;
    const selectedPartitionId = selectedPartition?.id;
    if (selectedPartitionId && rank.rankingPartition?.id !== selectedPartitionId) return false;
    if (!selectedExpansionId) return true;
    return (rank.includedExpansions ?? []).some((expansion) => expansion?.id === selectedExpansionId) || rank.expansion?.id === selectedExpansionId;
  });
  const bestAllStar = activeAllStarRanks[activeAllStarRanks.length - 1] ?? activeAllStarRanks[0] ?? null;
  const guild = character.guild ?? recentParses[0]?.guild ?? null;
  const classOptions = query.data.classOptions ?? [];
  const canManageClass = query.data.permissions?.canManageClass === true;
  const selectedClassDraft = classDraft || character.className || '';
  const saveClass = () => classMutation.mutate(selectedClassDraft ? selectedClassDraft : null);

  return (
    <>
      <section className="page-hero rankings-hero">
        <div className="content-wrapper py-4">
          <h1 className="display-6 fw-semibold mb-0">
            {character.name}
            {guild ? (
              <Link className="character-hero__guild-link d-block fs-5 fw-normal text-body-secondary text-decoration-none" to={serverGuildPath(serverSlug, guild)}>
                &lt;{guild.name}&gt;
              </Link>
            ) : null}
            <span className="page-hero__server d-block">{character.server?.name ?? 'Unknown Server'}</span>
          </h1>
          <p className="fs-3 mb-0">{character.className ?? 'Unknown class'}</p>
          {canManageClass ? (
            <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
              <select
                className="form-select form-select-sm w-auto"
                value={selectedClassDraft}
                onChange={(event) => setClassDraft(event.target.value)}
                aria-label="Character class"
              >
                <option value="">Unknown</option>
                {classOptions.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
              <button className="btn btn-sm btn-outline-light" type="button" onClick={saveClass} disabled={classMutation.isPending}>
                {classMutation.isPending ? 'Saving...' : 'Save Class'}
              </button>
              {classMutation.isError ? <span className="small text-danger">{classMutation.error instanceof Error ? classMutation.error.message : 'Could not save class'}</span> : null}
            </div>
          ) : null}
        </div>
        <div className="page-subnav character-expansion-subnav">
          <div className="content-wrapper">
            <ul className="nav secondary-tabs" aria-label="Character expansion navigation">
              {unlockedExpansionTabs.length === 0 ? (
                <li className="nav-item"><span className="nav-link disabled">No Expansions</span></li>
              ) : unlockedExpansionTabs.map((expansion) => (
                <li className="nav-item" key={expansionKey(expansion)}>
                  <button
                    className={`nav-link ${expansionKey(activeExpansion?.expansion) === expansionKey(expansion) ? 'active' : ''}`}
                    type="button"
                    onClick={() => setActiveExpansionKey(expansionKey(expansion))}
                  >
                    {expansion.name ?? 'Unknown'}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="page-subnav character-metric-subnav">
          <div className="content-wrapper">
            <ul className="nav secondary-tabs" role="tablist" aria-label="Character metric selector">
              <li className="nav-item" role="presentation">
                <button className={`nav-link ${metric === 'DAMAGE' ? 'active' : ''}`} type="button" onClick={() => setMetric('DAMAGE')}>Damage</button>
              </li>
              <li className="nav-item" role="presentation">
                <button className={`nav-link ${metric === 'HEALING' ? 'active' : ''}`} type="button" onClick={() => setMetric('HEALING')}>Healing</button>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <div className="page-main">
        <div className="content-wrapper">
          <section className="mb-4">
            <div className="card shadow-sm character-parses-card">
              <div className="card-header character-parses-card__header">
                <div className="d-flex flex-wrap align-items-end justify-content-between gap-3">
                  <div>
                    <h2 className="h5 mb-3">Parses</h2>
                    <div className="character-parse-summary d-flex flex-wrap gap-4 small">
                      <div>
                        <div className="text-body-secondary">Best Parse</div>
                        <div className="fw-semibold tabular-nums">{bestOverall?.percentile === null || bestOverall?.percentile === undefined ? 'N/A' : `${formatDecimal(bestOverall.percentile, 0)}%`}</div>
                      </div>
                      <div>
                        <div className="text-body-secondary">Avg Parse</div>
                        <div className="fw-semibold tabular-nums">{activeBossStats.length ? `${formatDecimal(avgPercentile, 1)}%` : 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-body-secondary">All-Star Rank</div>
                        <div className="fw-semibold tabular-nums">{bestAllStar ? `#${bestAllStar.rank}` : 'N/A'}</div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="form-label small text-uppercase text-body-secondary mb-1" htmlFor="character-partition-select">Partition</label>
                    <select
                      id="character-partition-select"
                      className="form-select form-select-sm w-auto"
                      value={partitionKey(selectedPartition)}
                      onChange={(event) => setActivePartitionKey(event.target.value)}
                      disabled={partitionOptions.length === 0}
                    >
                      {partitionOptions.length === 0 ? <option value="unknown">No partitions</option> : partitionOptions.map((partition) => (
                        <option key={partitionKey(partition)} value={partitionKey(partition)}>{partition.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="card-body p-4 border-bottom border-secondary border-opacity-25">
                <CharacterBossChart stats={activeBossStats} metric={metric} />
              </div>
              {activeBossStats.length === 0 ? (
                <div className="card-body text-body-secondary">No eligible {parseMetricLabel(metric).toLowerCase()} parses for this expansion yet.</div>
              ) : (
                <div className="table-responsive character-parses-table-wrap">
                  <table className="table table-dark table-sm table-hover align-middle mb-0 rankings-mini-table character-parses-table">
                    <thead>
                      <tr>
                        <th>Boss</th>
                        <th>Rank</th>
                        <th>Best Parse</th>
                        <th>Avg Parse</th>
                        <th>Guild</th>
                        <th className="text-end">{rateLabel}</th>
                        <th className="text-end">{amountLabel}</th>
                        <th className="text-end">{petLabel}</th>
                        <th>Duration</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeBossStats.map((stat) => {
                        const parse = stat.bestParse;
                        return (
                          <tr key={String(stat.boss?.id ?? stat.boss?.name ?? Math.random())}>
                            <td>
                              {stat.boss && parse ? (
                                <Link className="link-light fw-semibold text-decoration-none" to={serverCharacterBossParsesPath(serverSlug, character, stat.boss, metric)}>
                                  {stat.boss.name}
                                </Link>
                              ) : <span className="fw-semibold">{stat.boss?.name ?? 'Unknown boss'}</span>}
                            </td>
                            <td className="tabular-nums">
                              <EncounterValueLink parse={parse}>
                                {parse?.rank ? `#${parse.rank}` : 'N/A'}
                              </EncounterValueLink>
                            </td>
                            <td className="tabular-nums">
                              <EncounterValueLink parse={parse}>
                                {parse?.percentile === null || parse?.percentile === undefined ? 'N/A' : `${formatDecimal(parse.percentile, 0)}%`}
                              </EncounterValueLink>
                            </td>
                            <td className="tabular-nums">{stat.averagePercentile === null || stat.averagePercentile === undefined ? 'N/A' : `${formatDecimal(stat.averagePercentile, 1)}%`}</td>
                            <td>
                              {parse?.guild ? <Link className="link-light text-decoration-none" to={serverGuildPath(serverSlug, parse.guild)}>{parse.guild.name}</Link> : <span className="text-body-secondary">No guild</span>}
                            </td>
                            <td className="text-end tabular-nums">
                              <EncounterValueLink parse={parse}>
                                {parse ? formatDecimal(parseRate(parse, metric), 1) : 'N/A'}
                              </EncounterValueLink>
                            </td>
                            <td className="text-end tabular-nums">
                              <EncounterValueLink parse={parse}>
                                {parse ? formatNumber(parseAmount(parse, metric)) : 'N/A'}
                              </EncounterValueLink>
                            </td>
                            <td className="text-end tabular-nums">{parse ? formatNumber(parsePetAmount(parse, metric)) : 'N/A'}</td>
                            <td className="tabular-nums">{parse ? formatDuration(parse.durationSec) : 'N/A'}</td>
                            <td className="small text-body-secondary text-nowrap">{parse ? formatDateTime(parse.encounter.startedAt ?? parse.createdAt) : 'N/A'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
